import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, Clock, Video, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export const MeetingScheduler = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState("");
  const queryClient = useQueryClient();

  const { data: meetings, isLoading } = useQuery({
    queryKey: ["client-meetings"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("client_meetings")
        .select("*")
        .eq("client_id", user.id)
        .order("scheduled_at", { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDate || !selectedTime || !title) {
        throw new Error("Please fill in all required fields");
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const [hours, minutes] = selectedTime.split(':');
      const scheduledDate = new Date(selectedDate);
      scheduledDate.setHours(parseInt(hours), parseInt(minutes));

      const { error } = await supabase
        .from('client_meetings')
        .insert({
          client_id: user.id,
          title,
          description,
          scheduled_at: scheduledDate.toISOString(),
          created_by: user.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Meeting request sent successfully!");
      queryClient.invalidateQueries({ queryKey: ["client-meetings"] });
      setDialogOpen(false);
      setTitle("");
      setDescription("");
      setSelectedDate(undefined);
      setSelectedTime("");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to schedule meeting");
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; label: string }> = {
      scheduled: { variant: "default", label: "Scheduled" },
      confirmed: { variant: "default", label: "Confirmed" },
      completed: { variant: "secondary", label: "Completed" },
      cancelled: { variant: "destructive", label: "Cancelled" },
    };
    const { variant, label } = variants[status] || variants.scheduled;
    return <Badge variant={variant}>{label}</Badge>;
  };

  const upcomingMeetings = meetings?.filter(m =>
    new Date(m.scheduled_at) > new Date() && m.status !== 'cancelled'
  );
  const pastMeetings = meetings?.filter(m =>
    new Date(m.scheduled_at) <= new Date() || m.status === 'completed'
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Meetings</h2>
          <p className="text-muted-foreground mt-1">Schedule and manage your meetings with the team</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Request Meeting
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Schedule a Meeting</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Meeting Title *</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Project Review"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What would you like to discuss?"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !selectedDate && "text-muted-foreground"
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label>Time *</Label>
                  <Input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                  />
                </div>
              </div>

              <Button
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending}
                className="w-full"
              >
                {createMutation.isPending ? "Sending Request..." : "Request Meeting"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center py-12">Loading meetings...</div>
      ) : (
        <div className="space-y-6">
          {/* Upcoming Meetings */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Upcoming Meetings</h3>
            {!upcomingMeetings || upcomingMeetings.length === 0 ? (
              <Card className="p-8 text-center">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No upcoming meetings scheduled</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {upcomingMeetings.map((meeting: any) => (
                  <Card key={meeting.id} className="p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <Calendar className="h-5 w-5 text-primary mt-1" />
                          <div>
                            <h4 className="font-semibold text-lg">{meeting.title}</h4>
                            {meeting.description && (
                              <p className="text-sm text-muted-foreground mt-1">{meeting.description}</p>
                            )}
                            <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                {format(new Date(meeting.scheduled_at), "PPP 'at' p")}
                              </div>
                              {meeting.meeting_link && (
                                <div className="flex items-center gap-2">
                                  <Video className="h-4 w-4" />
                                  <a href={meeting.meeting_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                    Join Meeting
                                  </a>
                                </div>
                              )}
                              {meeting.location && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-4 w-4" />
                                  {meeting.location}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        {getStatusBadge(meeting.status)}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Past Meetings */}
          {pastMeetings && pastMeetings.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Past Meetings</h3>
              <div className="space-y-3">
                {pastMeetings.map((meeting: any) => (
                  <Card key={meeting.id} className="p-4 opacity-75">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">{meeting.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(meeting.scheduled_at), "PPP")}
                        </p>
                      </div>
                      {getStatusBadge(meeting.status)}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
